import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, ArrowLeft, ShieldCheck, CreditCard, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Register = () => {
  const navigate = useNavigate();
  
  // Custom styles for the ping animation
  const animationStyles = `
    @keyframes ping {
      75%, 100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
    }
  `;

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    batch: 'MORNING'
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    
    try {
      // 0. Load Razorpay Script
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        throw new Error('Razorpay SDK failed to load. Are you online?');
      }

      // 1. Create Order on Backend
      const response = await fetch('https://nimworkshop-backend26.onrender.com/api/register/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (data.status !== 'success') {
        throw new Error(data.message || 'Order creation failed');
      }

      // 2. Open Razorpay Checkout
      const options = {
        key: data.key,
        amount: data.amount,
        currency: "INR",
        name: "Nim Technology",
        description: "Workshop Registration Fee",
        order_id: data.order_id,
        handler: async function (response) {
          // 3. Verify Payment on Backend
          const verifyRes = await fetch('https://nimworkshop-backend26.onrender.com/api/verify/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          const verifyData = await verifyRes.json();

          if (verifyData.status === 'success') {
            setIsLoading(false);
            navigate('/success', { state: { batch: formData.batch } });
          } else {
            alert('Payment verification failed. Please contact support.');
            setIsLoading(false);
          }
        },
        prefill: {
          name: formData.fullName,
          email: formData.email,
          contact: formData.phone
        },
        theme: { color: "#6366f1" },
        modal: {
          ondismiss: function() {
            setIsLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      setIsLoading(false);
      console.error('Registration Error:', error);
      alert(`Error: ${error.message || 'Something went wrong'}. Please check if you have a stable internet connection.`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="register-page" style={{ 
      background: 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <style>{animationStyles}</style>
      {/* Premium Background Elements */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        right: '-10%',
        width: '50%',
        height: '50%',
        background: 'rgba(99, 102, 241, 0.1)',
        filter: 'blur(120px)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '-10%',
        left: '-10%',
        width: '50%',
        height: '50%',
        background: 'rgba(59, 130, 246, 0.1)',
        filter: 'blur(120px)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }}></div>

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <motion.button 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')} 
          className="back-btn"
        >
          <ArrowLeft size={20} /> Back to Event
        </motion.button>

        <div className="register-layout">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="register-card"
          >
            <div className="card-header">
              <h2>Complete Registration</h2>
              <p>Secure your spot for the AI Agents Workshop</p>
            </div>

            <form onSubmit={handleSubmit} className="register-form">
              <div className="form-group">
                <label>Full Name</label>
                <div className={`input-wrapper ${errors.fullName ? 'error' : ''}`}>
                  <User size={18} />
                  <input 
                    type="text" 
                    name="fullName"
                    placeholder="John Doe" 
                    value={formData.fullName}
                    onChange={handleChange}
                  />
                </div>
                {errors.fullName && <span className="error-text">{errors.fullName}</span>}
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <div className={`input-wrapper ${errors.email ? 'error' : ''}`}>
                  <Mail size={18} />
                  <input 
                    type="email" 
                    name="email"
                    placeholder="john@example.com" 
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <div className={`input-wrapper ${errors.phone ? 'error' : ''}`}>
                  <Phone size={18} />
                  <input 
                    type="tel" 
                    name="phone"
                    placeholder="9876543210" 
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>

              <div className="form-group mt-8">
                <label className="text-sm font-bold text-slate-700 mb-4 block">Select Workshop Session</label>
                <div className="session-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <label 
                    onClick={() => setFormData(prev => ({ ...prev, batch: 'MORNING' }))}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '20px',
                      borderRadius: '16px',
                      border: `2px solid ${formData.batch === 'MORNING' ? '#4f46e5' : '#f1f5f9'}`,
                      background: formData.batch === 'MORNING' ? '#f5f3ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      gap: '16px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                  >
                    <div style={{
                      marginTop: '4px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: `2px solid ${formData.batch === 'MORNING' ? '#4f46e5' : '#cbd5e1'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {formData.batch === 'MORNING' && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#4f46e5' }}></div>}
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '18px', color: '#1e293b', marginBottom: '4px', lineHeight: '1.2' }}>Morning Session</div>
                      <div style={{ fontWeight: 500, fontSize: '14px', color: '#64748b' }}>10:00 AM – 01:00 PM</div>
                    </div>
                  </label>

                  <label 
                    onClick={() => setFormData(prev => ({ ...prev, batch: 'EVENING' }))}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      padding: '20px',
                      borderRadius: '16px',
                      border: `2px solid ${formData.batch === 'EVENING' ? '#4f46e5' : '#f1f5f9'}`,
                      background: formData.batch === 'EVENING' ? '#f5f3ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      gap: '16px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                  >
                    <div style={{
                      marginTop: '4px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: `2px solid ${formData.batch === 'EVENING' ? '#4f46e5' : '#cbd5e1'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {formData.batch === 'EVENING' && <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#4f46e5' }}></div>}
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '18px', color: '#1e293b', marginBottom: '4px', lineHeight: '1.2' }}>Evening Session</div>
                      <div style={{ fontWeight: 500, fontSize: '14px', color: '#64748b' }}>03:00 PM – 06:00 PM</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="payment-info">
                <div className="price-breakdown">
                  <span>Registration Fee</span>
                  <span>₹149.00</span>
                </div>
                <div className="total">
                  <span>Total Payable</span>
                  <span>₹149.00</span>
                </div>
              </div>

              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Processing...
                  </>
                ) : (
                  <>
                    Register & Pay ₹149 <CreditCard size={18} />
                  </>
                )}
              </button>

              <div className="secure-badge">
                <ShieldCheck size={14} /> Secure Payment powered by Razorpay
              </div>
            </form>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="order-summary"
          >
            <h3>Workshop Details</h3>
            <div className="summary-details">
              <div className="summary-item">
                <span className="label">Event</span>
                <span className="value">Building Autonomous AI Agents Using LLMs</span>
              </div>
              <div className="summary-item">
                <span className="label">Date</span>
                <span className="value">May 6, 2026</span>
              </div>
              <div className="summary-item">
                <span className="label">Venue</span>
                <span className="value">57/57b, Rajakkamangalam Rd, Nagercoil, Tamil Nadu 629170</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {isLoading && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="loading-overlay"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgba(15, 23, 42, 0.8)',
              backdropFilter: 'blur(12px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="glass-loading-card"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                padding: '3rem',
                borderRadius: '2.5rem',
                textAlign: 'center',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div className="payment-animation-wrapper" style={{ position: 'relative', height: '100px', marginBottom: '2rem' }}>
                <div className="pulse-ring" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '80px', height: '80px', border: '3px solid #6366f1', borderRadius: '50%', animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite' }}></div>
                <div className="spinner-center" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '60px', height: '60px', background: '#6366f1', borderRadius: '20px', display: 'flex', alignHover: 'center', justifyContent: 'center', alignItems: 'center', color: 'white', boxShadow: '0 10px 20px rgba(99, 102, 241, 0.4)' }}>
                  <CreditCard size={32} />
                </div>
              </div>

              <h3 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.5rem', fontFamily: 'Outfit, sans-serif' }}>Securing Transaction</h3>
              <p style={{ color: '#64748b', fontSize: '0.9375rem', marginBottom: '2rem' }}>Please do not refresh. We are connecting to the secure payment server...</p>
              
              <div className="security-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#f0fdf4', color: '#16a34a', borderRadius: '2rem', fontSize: '0.75rem', fontWeight: 700, border: '1px solid #dcfce7' }}>
                <ShieldCheck size={14} /> 256-BIT ENCRYPTED
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Register;
